import React from 'react';
import { ArrowRight, CheckCircle, Settings, Zap, Shield, Clock, AlertTriangle, Monitor } from 'lucide-react';
import Layout from '../../layouts/Layout';
import CTA from '../../components/CTA';
import { Link } from 'react-router-dom';

const MaintenanceSupportPage = () => {
    const supportServices = [
        {
            icon: <Monitor className="w-6 h-6" />,
            title: "24/7 System Monitoring",
            description: "Proactive monitoring of your systems with real-time alerts and immediate response to issues.",
            features: ["Server monitoring", "Performance tracking", "Uptime monitoring", "Security scanning"]
        },
        {
            icon: <Settings className="w-6 h-6" />,
            title: "Regular Maintenance",
            description: "Scheduled maintenance including updates, backups, and performance optimisation.",
            features: ["Software updates", "Security patches", "Database optimisation", "Performance tuning"]
        },
        {
            icon: <Shield className="w-6 h-6" />,
            title: "Security Management",
            description: "Comprehensive security monitoring and management to protect your digital assets.",
            features: ["Vulnerability scanning", "Security updates", "Malware protection", "Firewall management"]
        },
        {
            icon: <AlertTriangle className="w-6 h-6" />,
            title: "Emergency Support",
            description: "Rapid response emergency support for critical issues and system failures.",
            features: ["Emergency hotline", "Rapid response", "Issue resolution", "System recovery"]
        }
    ];

    const supportPlans = [
        {
            name: "Essential",
            price: "From £149/month",
            annualPrice: "£1,428/year",
            savings: "Save £360",
            description: "Perfect for small businesses with basic maintenance needs",
            features: [
                "Business hours support (9am-5pm)",
                "Monthly system updates",
                "Basic monitoring and alerts",
                "Email and phone support",
                "Monthly backup verification",
                "Security patch management"
            ],
            responseTime: "4 hours",
            recommended: false
        },
        {
            name: "Professional",
            price: "From £299/month",
            annualPrice: "£2,868/year",
            savings: "Save £720",
            description: "Comprehensive support for growing businesses",
            features: [
                "Extended hours support (8am-8pm)",
                "Weekly system updates",
                "Advanced monitoring and alerts",
                "Priority email and phone support",
                "Weekly backup verification",
                "Performance optimisation",
                "Security vulnerability scanning",
                "Monthly performance reports"
            ],
            responseTime: "2 hours",
            recommended: true
        },
        {
            name: "Enterprise",
            price: "From £599/month",
            annualPrice: "£5,748/year",
            savings: "Save £1,440",
            description: "Premium support for mission-critical applications",
            features: [
                "24/7 dedicated support",
                "Real-time system monitoring",
                "Proactive issue prevention",
                "Dedicated account manager",
                "Daily backup verification",
                "Advanced security monitoring",
                "Custom SLA agreements",
                "Emergency response team",
                "Quarterly business reviews"
            ],
            responseTime: "30 minutes",
            recommended: false
        }
    ];

    const maintenanceTypes = [
        {
            title: "WordPress Maintenance",
            description: "Comprehensive WordPress site maintenance and optimisation",
            services: [
                "Core, theme, and plugin updates",
                "Security hardening and monitoring",
                "Performance optimisation",
                "Content backup and recovery",
                "Malware scanning and removal",
                "SEO performance monitoring"
            ]
        },
        {
            title: "Web Application Maintenance",
            description: "Full-stack maintenance for custom web applications",
            services: [
                "Framework and dependency updates",
                "Database optimisation and tuning",
                "Server configuration management",
                "Application performance monitoring",
                "Security patch management",
                "Load testing and optimisation"
            ]
        },
        {
            title: "Mobile App Maintenance",
            description: "Ongoing support for iOS and Android applications",
            services: [
                "OS compatibility updates",
                "App store compliance management",
                "Performance monitoring and tuning",
                "User experience optimisation",
                "Crash monitoring and fixing",
                "Feature updates and enhancements"
            ]
        },
        {
            title: "E-commerce Maintenance",
            description: "Specialised maintenance for online stores and marketplaces",
            services: [
                "Payment gateway updates",
                "Inventory system monitoring",
                "Order processing optimisation",
                "Shopping cart performance tuning",
                "Security compliance monitoring",
                "Conversion rate optimisation"
            ]
        }
    ];

    const monitoringFeatures = [
        {
            icon: <Monitor className="w-6 h-6" />,
            title: "Performance Monitoring",
            description: "Real-time tracking of system performance, load times, and resource usage with detailed analytics."
        },
        {
            icon: <Shield className="w-6 h-6" />,
            title: "Security Monitoring",
            description: "Continuous security scanning for vulnerabilities, malware, and suspicious activities."
        },
        {
            icon: <Clock className="w-6 h-6" />,
            title: "Uptime Monitoring",
            description: "24/7 availability monitoring with instant alerts for any downtime or service interruptions."
        },
        {
            icon: <Zap className="w-6 h-6" />,
            title: "Automated Backups",
            description: "Scheduled automated backups with secure storage and quick recovery options when needed."
        }
    ];

    const benefits = [
        "99.9% uptime guarantee with proactive monitoring",
        "Reduced security risks with regular updates and patches",
        "Improved performance through ongoing optimisation",
        "Cost-effective compared to in-house maintenance teams",
        "Peace of mind with 24/7 expert support available",
        "Regular reporting on system health and performance",
        "Quick resolution of issues before they impact users",
        "Compliance with industry standards and regulations"
    ];

    const process = [
        {
            step: "01",
            title: "Initial Assessment",
            description: "Comprehensive evaluation of your current systems, infrastructure, and maintenance requirements."
        },
        {
            step: "02",
            title: "Custom Plan Creation",
            description: "Development of a tailored maintenance plan that meets your specific needs and budget."
        },
        {
            step: "03",
            title: "Monitoring Setup",
            description: "Implementation of monitoring tools and alert systems to track system health and performance."
        },
        {
            step: "04",
            title: "Ongoing Maintenance",
            description: "Regular maintenance activities including updates, backups, security scans, and optimisation."
        },
        {
            step: "05",
            title: "Reporting & Optimisation",
            description: "Monthly reports on system performance with recommendations for improvements and optimisations."
        }
    ];

    return (
        <Layout
            title="Maintenance & Support Services"
            description="Professional website and application maintenance services in the UK. 24/7 monitoring, security updates, performance optimisation, and technical support for WordPress, web apps, and mobile applications."
            keywords="website maintenance UK, WordPress support, web application maintenance, 24/7 monitoring, technical support, website security, performance optimisation"
            path="/services/maintenance-support"
        >
            <div className="min-h-screen bg-white">
                {/* Hero Section */}
                <section className="relative pt-32 pb-20 px-6 bg-gradient-to-br from-slate-50 to-slate-100">
                    <div className="max-w-6xl mx-auto">
                        <div className="grid lg:grid-cols-2 gap-12 items-center">
                            <div>
                                <div className="inline-flex items-center px-4 py-2 bg-slate-900 text-white rounded-full text-sm font-medium mb-8">
                                    <Settings className="w-4 h-4 mr-2" />
                                    Maintenance & Support
                                </div>

                                <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-8 leading-tight">
                                    Keep Your Systems
                                    <span className="block text-slate-700 mt-2">Running Perfectly</span>
                                </h1>

                                <p className="text-xl text-slate-600 mb-10 leading-relaxed">
                                    Comprehensive maintenance and support services to ensure your websites, applications,
                                    and systems run smoothly. From 24/7 monitoring to emergency support, we've got you covered
                                    with proactive maintenance that prevents issues before they occur.
                                </p>

                                <div className="flex flex-col sm:flex-row gap-4">
                                    <Link 
                                        to="#support-plans"
                                        className="bg-slate-900 text-white px-8 py-4 rounded-xl font-semibold hover:bg-slate-800 transition-all hover:shadow-lg flex items-center justify-center"
                                    >
                                        Get Support Plan <ArrowRight className="ml-2 w-5 h-5" />
                                    </Link>
                                    {/* <button className="border-2 border-slate-200 text-slate-700 px-8 py-4 rounded-xl font-semibold hover:border-slate-300 hover:bg-slate-50 transition-all">
                                        Emergency Support
                                    </button> */}
                                </div>
                            </div>

                            <div className="relative">
                                <div className="bg-white rounded-3xl p-8 shadow-2xl border border-slate-200">
                                    <div className="space-y-6">
                                        <div className="flex items-center justify-between">
                                            <span className="text-slate-600">System Uptime</span>
                                            <span className="text-slate-900 font-semibold">99.9%</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-slate-600">Response Time</span>
                                            <span className="text-slate-900 font-semibold">&lt;30 mins</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-slate-600">Security Updates</span>
                                            <span className="text-slate-900 font-semibold">Weekly</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-slate-600">Support Coverage</span>
                                            <span className="text-slate-900 font-semibold">24/7</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Support Services Section */}
                <section className="py-24 bg-white">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">Comprehensive Support Services</h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                Professional maintenance and support to keep your digital assets secure, updated, and performing optimally
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 gap-8">
                            {supportServices.map((service, index) => (
                                <div key={index} className="bg-slate-50 rounded-2xl p-8 border border-slate-200 hover:shadow-lg transition-shadow">
                                    <div className="w-16 h-16 bg-slate-900 rounded-xl flex items-center justify-center text-white mb-6">
                                        {service.icon}
                                    </div>
                                    <h3 className="text-2xl font-bold text-slate-900 mb-4">{service.title}</h3>
                                    <p className="text-slate-600 mb-6 leading-relaxed">{service.description}</p>
                                    <div className="grid grid-cols-2 gap-3">
                                        {service.features.map((feature, featureIndex) => (
                                            <div key={featureIndex} className="flex items-center">
                                                <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                                                <span className="text-slate-700 text-sm">{feature}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Support Plans Section */}
                <section className="py-24 bg-slate-50" id="support-plans">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">Support Plans</h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                Choose the right level of support for your business needs and budget. Save up to £960 with annual billing.
                            </p>
                        </div>

                        <div className="grid md:grid-cols-3 gap-8">
                            {supportPlans.map((plan, index) => (
                                <div key={index} className={`bg-white rounded-2xl p-8 border-2 hover:shadow-lg transition-all ${plan.recommended ? 'border-slate-900 ring-4 ring-slate-100' : 'border-slate-200'
                                    }`}>
                                    {plan.recommended && (
                                        <div className="bg-slate-900 text-white text-sm font-medium px-4 py-2 rounded-full inline-block mb-6">
                                            Most Popular
                                        </div>
                                    )}
                                    <h3 className="text-2xl font-bold text-slate-900 mb-2">{plan.name}</h3>
                                    <div className="text-3xl font-bold text-slate-900 mb-2">{plan.price}</div>
                                    <div className="text-lg text-slate-600 mb-2">{plan.annualPrice}</div>
                                    <div className="text-sm text-green-600 font-medium mb-4">{plan.savings}</div>
                                    <p className="text-slate-600 mb-6">{plan.description}</p>

                                    <div className="space-y-3 mb-8">
                                        {plan.features.map((feature, featureIndex) => (
                                            <div key={featureIndex} className="flex items-start">
                                                <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                                                <span className="text-slate-700">{feature}</span>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="text-center mb-6">
                                        <div className="text-sm text-slate-600">Response Time</div>
                                        <div className="text-lg font-bold text-slate-900">{plan.responseTime}</div>
                                    </div>

                                    <div className="flex flex-col gap-3">
                                        <Link 
                                            to="/contact"
                                            className={`w-full py-3 px-6 rounded-xl font-semibold transition-all text-center ${plan.recommended
                                                ? 'bg-slate-900 text-white hover:bg-slate-800'
                                                : 'border-2 border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                                            }`}
                                        >
                                            Get Custom Quote
                                        </Link>
                                        <Link 
                                            to="/schedule-consultation"
                                            className="text-slate-600 hover:text-slate-900 font-medium text-sm transition-colors text-center"
                                        >
                                            Schedule Consultation
                                        </Link>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Maintenance Types Section */}
                <section className="py-24 bg-white">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">Maintenance Services by Platform</h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                Specialised maintenance services tailored to different platforms and technologies
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 gap-8">
                            {maintenanceTypes.map((type, index) => (
                                <div key={index} className="bg-slate-50 rounded-2xl p-8 border border-slate-200 hover:shadow-lg transition-shadow">
                                    <h3 className="text-2xl font-bold text-slate-900 mb-4">{type.title}</h3>
                                    <p className="text-slate-600 mb-6 leading-relaxed">{type.description}</p>
                                    <div className="space-y-3">
                                        {type.services.map((service, serviceIndex) => (
                                            <div key={serviceIndex} className="flex items-center">
                                                <CheckCircle className="w-4 h-4 text-green-600 mr-3" />
                                                <span className="text-slate-700">{service}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Monitoring Features Section */}
                <section className="py-24 bg-slate-50">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">Advanced Monitoring & Alerting</h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                Proactive monitoring that identifies and resolves issues before they impact your business
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                            {monitoringFeatures.map((feature, index) => (
                                <div key={index} className="bg-white rounded-2xl p-8 border border-slate-200 hover:shadow-lg transition-shadow">
                                    <div className="w-16 h-16 bg-slate-900 rounded-xl flex items-center justify-center text-white mb-6">
                                        {feature.icon}
                                    </div>
                                    <h3 className="text-xl font-bold text-slate-900 mb-4">{feature.title}</h3>
                                    <p className="text-slate-600 leading-relaxed">{feature.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Process Section */}
                <section className="py-24 bg-white">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">Our Maintenance Process</h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                A systematic approach to keeping your systems secure, updated, and performing optimally
                            </p>
                        </div>

                        <div className="space-y-8">
                            {process.map((step, index) => (
                                <div key={index} className="flex items-start">
                                    <div className="flex-shrink-0 w-16 h-16 bg-slate-900 rounded-xl flex items-center justify-center text-white font-bold text-lg mr-6">
                                        {step.step}
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-2xl font-bold text-slate-900 mb-4">{step.title}</h3>
                                        <p className="text-lg text-slate-600 leading-relaxed">{step.description}</p>
                                    </div>
                                    {/* {index < process.length - 1 && (
                                        <ChevronRight className="w-6 h-6 text-slate-400 ml-6" />
                                    )} */}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Benefits Section */}
                <section className="py-24 bg-slate-50">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="grid lg:grid-cols-2 gap-16 items-center">
                            <div>
                                <h2 className="text-4xl font-bold text-slate-900 mb-8">Why Choose Our Maintenance Services?</h2>
                                <p className="text-xl text-slate-600 mb-10 leading-relaxed">
                                    Our comprehensive maintenance and support services ensure your digital assets remain
                                    secure, performant, and up-to-date, allowing you to focus on growing your business.
                                </p>

                                <div className="space-y-4">
                                    {benefits.map((benefit, index) => (
                                        <div key={index} className="flex items-start">
                                            <CheckCircle className="w-6 h-6 text-green-600 mr-4 flex-shrink-0 mt-0.5" />
                                            <span className="text-slate-700 text-lg">{benefit}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-lg">
                                <h3 className="text-2xl font-bold text-slate-900 mb-6">Service Level Guarantees</h3>
                                <div className="space-y-6">
                                    <div className="flex items-center justify-between">
                                        <span className="text-slate-600">Uptime Guarantee</span>
                                        <span className="text-slate-900 font-semibold">99.9%</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-slate-600">Emergency Response</span>
                                        <span className="text-slate-900 font-semibold">30 minutes</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-slate-600">Security Updates</span>
                                        <span className="text-slate-900 font-semibold">Within 24h</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-slate-600">Backup Frequency</span>
                                        <span className="text-slate-900 font-semibold">Daily</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-slate-600">Issue Resolution</span>
                                        <span className="text-slate-900 font-semibold">Same Day</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Performance Stats */}
                <section className="py-24 bg-white">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="bg-slate-900 rounded-3xl p-12 text-white">
                            <div className="text-center mb-12">
                                <h2 className="text-4xl font-bold mb-6">Maintenance Performance</h2>
                                <p className="text-xl text-slate-300 max-w-3xl mx-auto">
                                    Our maintenance services consistently deliver exceptional reliability and performance
                                </p>
                            </div>

                            <div className="grid md:grid-cols-4 gap-8 text-center">
                                <div>
                                    <div className="text-4xl font-bold mb-2">99.9%</div>
                                    <div className="text-slate-300">Uptime Achieved</div>
                                </div>
                                <div>
                                    <div className="text-4xl font-bold mb-2">30min</div>
                                    <div className="text-slate-300">Response Time</div>
                                </div>
                                <div>
                                    <div className="text-4xl font-bold mb-2">24/7</div>
                                    <div className="text-slate-300">Monitoring</div>
                                </div>
                                <div>
                                    <div className="text-4xl font-bold mb-2">100%</div>
                                    <div className="text-slate-300">Client Satisfaction</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Emergency Support CTA */}
                <section className="py-24 bg-slate-50">
                    <div className="max-w-4xl mx-auto px-6 text-center">
                        <div className="bg-red-50 border border-red-200 rounded-2xl p-8">
                            <AlertTriangle className="w-16 h-16 text-red-600 mx-auto mb-6" />
                            <h2 className="text-3xl font-bold text-slate-900 mb-4">Need Emergency Support?</h2>
                            <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
                                If you're experiencing a critical issue with your website or application, our emergency
                                support team is available 24/7 to help resolve problems quickly.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <Link to="tel:+442070460289" className="bg-red-600 text-white text-center px-8 py-4 rounded-xl font-semibold hover:bg-red-700 transition-all hover:shadow-lg flex items-center justify-center">
                                    Call Emergency Support
                                </Link>
                                {/* <button className="border-2 border-slate-200 text-slate-700 px-8 py-4 rounded-xl font-semibold hover:border-slate-300 hover:bg-slate-50 transition-all">
                                    Submit Support Ticket
                                </button> */}
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <CTA />
            </div>
        </Layout>
    );
};

export default MaintenanceSupportPage;